/**
 * Set Organization Structure Page (Step 1)
 * Manages facilities and locations creation.
 * Clean code: single responsibility, composed hooks, presentational components.
 * SOLID: page orchestrates only; logic in hooks and constants.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AddFacilityModal } from '../../components/organizationFacilities/AddFacilityModal';
import { DuplicateFacilityModal } from '../../components/organizationFacilities/DuplicateFacilityModal';
import { DeleteFacilityModal } from '../../components/organizationFacilities/DeleteFacilityModal';
import { FacilitiesSearchList } from '../../components/organizationFacilities/FacilitiesSearchList';
import { LocationsPanel } from '../../components/organizationLocations/LocationsPanel';
import { WizardActionBar } from '../../components/organizationWizard/WizardActionBar';
import { useFeedbackContext } from '../../context';
import MainPageLayout from '../../components/common/MainPageLayout';
import ROUTES from '../../constants/routes';
import {
  useSetOrganizationStructure,
  useFacilityModals,
  useFacilityActions,
  useFacilityHandlersWithFeedback,
} from './hooks';
import { PageHeader, AddFacilityButton, LocationsColumnHeader } from './components';
import {
  WIZARD_STEP,
  PANEL_CARD_CLASS,
  ERROR_MESSAGES,
  buildDuplicateFacilityOptions,
} from './constants';

function SetOrganizationStructurePage() {
  const navigate = useNavigate();
  const { showToast } = useFeedbackContext();

  const organizationId =
    useSelector((state) => state.auth.user?.organization) || localStorage.getItem('organizationId');

  const {
    facilitiesFromList,
    handleFacilitiesChange,
    validateCanContinue,
    refetchFacilities,
    queryScope,
  } = useSetOrganizationStructure();

  const modals = useFacilityModals();
  const {
    isAddModalOpen,
    facilityToEdit,
    openAddModal,
    closeAddModal,
    openEditModal,
    isDuplicateModalOpen,
    facilityToDuplicate,
    facilityLocationsCount,
    openDuplicateModal,
    closeDuplicateModal,
    isDeleteModalOpen,
    facilityToDelete,
    openDeleteModal,
    closeDeleteModal,
  } = modals;

  const actions = useFacilityActions(showToast, refetchFacilities);
  const {
    handleAddFacilityWithFeedback,
    handleUpdateFacilityWithFeedback,
    handleDeleteFacilityWithFeedback,
    handleDuplicateConfirm,
    showErrorToast,
  } = useFacilityHandlersWithFeedback(actions);

  const [selectedFacilityIds, setSelectedFacilityIds] = useState([]);

  // Keep selection in sync with list: remove ids that no longer exist (e.g. after delete)
  const facilityIdsKey = useMemo(
    () =>
      facilitiesFromList
        .map((f) => f.id)
        .sort()
        .join(','),
    [facilitiesFromList],
  );
  useEffect(() => {
    const ids = facilityIdsKey ? facilityIdsKey.split(',').filter(Boolean) : [];
    setSelectedFacilityIds((prev) => prev.filter((id) => ids.includes(id)));
  }, [facilityIdsKey]);

  const handleSelectAllFacilities = useCallback(() => {
    setSelectedFacilityIds((prev) =>
      prev.length === facilitiesFromList.length ? [] : facilitiesFromList.map((f) => f.id),
    );
  }, [facilitiesFromList]);

  const handleSelectFacility = useCallback((facilityId) => {
    setSelectedFacilityIds((prev) =>
      prev.includes(facilityId) ? prev.filter((id) => id !== facilityId) : [...prev, facilityId],
    );
  }, []);

  const handleDuplicateConfirmWithFeedback = useCallback(
    async (newName) => {
      if (!facilityToDuplicate) return;
      const options = buildDuplicateFacilityOptions(newName);
      try {
        await handleDuplicateConfirm(facilityToDuplicate.id, options, newName, closeDuplicateModal);
      } catch (error) {
        showErrorToast(ERROR_MESSAGES.DUPLICATE_FACILITY)(error);
        throw error;
      }
    },
    [facilityToDuplicate, handleDuplicateConfirm, closeDuplicateModal, showErrorToast],
  );

  return (
    <MainPageLayout customHeaderProps={{ hideBackButton: true }}>
      <div className="w-full">
        <div className="max-w-[1400px] mx-auto px-6 pt-4 pb-32">
          <PageHeader />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,360px)_1fr] gap-6 lg:gap-8 mb-8">
            <div className={`${PANEL_CARD_CLASS} p-6`}>
              <FacilitiesSearchList
                variant="infinite"
                queryScope={queryScope}
                organizationId={organizationId}
                selectedFacilityIds={selectedFacilityIds}
                onSelectFacility={handleSelectFacility}
                onSelectAll={handleSelectAllFacilities}
                onFacilitiesChange={handleFacilitiesChange}
                onEditFacility={openEditModal}
                onDuplicateFacility={openDuplicateModal}
              >
                <AddFacilityButton onClick={openAddModal} />
              </FacilitiesSearchList>
            </div>

            <div className={`${PANEL_CARD_CLASS} px-6 pt-6 pb-8 min-h-[400px]`}>
              <LocationsColumnHeader />
              <LocationsPanel
                organizationId={organizationId}
                queryScope={queryScope}
                selectedFacilityIds={selectedFacilityIds}
                onLocationsChange={validateCanContinue}
                showCleaningStandards={false}
              />
            </div>
          </div>

          <WizardActionBar
            currentStep={WIZARD_STEP.CURRENT}
            totalSteps={WIZARD_STEP.TOTAL}
            onContinue={() => navigate(ROUTES.ORGANIZATION_CONFIGURATION)}
            stepName={WIZARD_STEP.NAME}
            continueText="Back to overview"
          />
        </div>
      </div>

      <AddFacilityModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSubmit={handleAddFacilityWithFeedback}
        onUpdate={handleUpdateFacilityWithFeedback}
        onDeleteClick={openDeleteModal}
        organizationId={organizationId}
        facilityToEdit={facilityToEdit}
      />

      {facilityToDuplicate && (
        <DuplicateFacilityModal
          isOpen={isDuplicateModalOpen}
          onClose={closeDuplicateModal}
          onConfirm={handleDuplicateConfirmWithFeedback}
          facility={facilityToDuplicate}
          locationsCount={facilityLocationsCount}
          hasCleaningStandards={false}
          hasSoilLoadLevels={false}
          hasWaterChanges={false}
        />
      )}

      {facilityToDelete && (
        <DeleteFacilityModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={() => {
            const idToDelete = facilityToDelete.id;
            handleDeleteFacilityWithFeedback(idToDelete).then(() => {
              setSelectedFacilityIds((prev) => prev.filter((id) => id !== idToDelete));
            });
          }}
          facility={facilityToDelete}
          locationsCount={facilityToDelete.locationCount || 0}
        />
      )}
    </MainPageLayout>
  );
}

export default SetOrganizationStructurePage;
